import { defineStore } from "pinia";
import date from "@/libs/date";
import type { Currency, Group } from "@/types/transaction";
import type { Ledger } from "@/types/account";
import type { RRule } from "rrule";
import type { Dayjs } from "dayjs";
import Income from "@/classes/Income.ts";
import Expense from "@/classes/Expense.ts";
import type { TransactionProperties } from "@/classes/Transaction.ts";

export type Params = {
  amount: number;
  applyToIncomeTax?: boolean;
  currency: Currency;
  date?: Dayjs;
  description: string;
  group?: Group | null;
  isDisabled?: boolean;
  ledger: Ledger;
  name: string;
  recurrence?: RRule | null;
  settlesReservation?: string | null;
};

export const useEditTransaction = defineStore("EditTransaction", {
  state: () => ({
    amount: 0,
    applyToIncomeTax: false,
    currency: "EUR" as Currency,
    // A transaction is dated by calendar day, matching what the factory reads back.
    date: date().startOf("day"),
    description: "",
    group: null as Group | null,
    isDisabled: false,
    ledger: undefined as Ledger | undefined,
    name: "",
    recurrence: null as RRule | null,
    settlesReservation: null as string | null,
    type: "expense" as "expense" | "income",
  }),

  actions: {
    hydrate(params: Params) {
      this.amount = params.amount;
      this.applyToIncomeTax = params.applyToIncomeTax ?? false;
      this.currency = params.currency;
      this.date = params.date ?? date();
      this.description = params.description;
      this.isDisabled = params.isDisabled ?? false;
      this.group = params.group ?? null;
      this.ledger = params.ledger;
      this.name = params.name;
      this.recurrence = params.recurrence ?? null;
      this.settlesReservation = params.settlesReservation ?? null;
    },

    toTransaction(id?: number): Expense | Income {
      const properties: TransactionProperties = {
        amount: this.amount,
        applyToIncomeTax: this.applyToIncomeTax,
        currency: this.currency,
        date: this.date,
        description: this.description,
        id,
        isDisabled: this.isDisabled,
        group: this.group,
        ledger: this.ledger!,
        name: this.name,
        recurrence: this.recurrence as RRule | null,
        settlesReservation: this.settlesReservation,
        tax: null,
      };

      return this.type === "expense" ? new Expense(properties) : new Income(properties);
    },
  },
});
